// frontend/src/ProcessSetup.tsx - FIXED: All syntax errors resolved

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { API_URL } from './apiConfig';

interface ProcessSetupProps {
  processId: number;
  processName: string;
  onComplete: () => void;
  onBack: () => void;
  viewMode?: 'setup' | 'summary'; // New prop to control view mode
}

interface ComplianceCriterion {
  description: string;
  order_index: number;
}

interface MandatoryRequirement {
  requirement_text: string;
  order_index: number;
}

interface MainCriterion {
  name: string;
  description: string;
  weight_percentage: number;
  minimum_threshold?: number;
  order_index: number;
  sub_criteria: SubCriterion[];
}

interface SubCriterion {
  name: string;
  description: string;
  weight_percentage: number;
  minimum_threshold?: number;
  order_index: number;
}


const styles = {
    mainContainer: {
      minHeight: '100vh',
      background: 'var(--background-brand-gradient)',
      fontFamily: 'var(--font-family-primary)',
      color: 'var(--color-text-primary)',
      padding: 'var(--spacing-lg)'
    },
    contentWrapper: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    pageTitle: {
      fontSize: 'var(--font-size-headline-large)',
      fontWeight: 'var(--font-weight-headline)',
      color: 'var(--color-text-on-gradient)',
      marginBottom: 'var(--spacing-xl)',
      textAlign: 'center' as 'center'
    },
    card: {
      backgroundColor: 'var(--color-surface)',
      padding: 'var(--spacing-xl)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
      border: '1px solid var(--color-surface-variant)',
      marginBottom: 'var(--spacing-lg)'
    },
    button: {
      padding: 'var(--spacing-sm) var(--spacing-md)',
      background: 'var(--color-primary)',
      color: 'white',
      border: 'none',
      borderRadius: 'var(--border-radius-md)',
      fontSize: 'var(--font-size-sm)',
      fontWeight: 'var(--font-weight-medium)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 'var(--spacing-xs)'
    },
    buttonSecondary: {
      background: 'var(--color-surface-variant)',
      color: 'var(--color-text-primary)',
      border: '2px solid var(--color-primary)'
    },
    percentageDisplay: {
      padding: 'var(--spacing-md)',
      borderRadius: 'var(--radius-md)',
      fontSize: '1rem',
      fontWeight: 'bold',
      textAlign: 'center' as const,
      margin: 'var(--spacing-md) 0'
    },
    percentageValid: {
      backgroundColor: 'var(--color-success-light)',
      color: 'var(--color-success)',
      border: '2px solid var(--color-success)'
    },
    percentageInvalid: {
      backgroundColor: 'var(--color-error-light)',
      color: 'var(--color-error)',
      border: '2px solid var(--color-error)'
    }
};

const ProcessSetup: React.FC<ProcessSetupProps> = ({ processId, processName, onComplete, onBack, viewMode = 'setup' }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [currentViewMode, setCurrentViewMode] = useState<'setup' | 'summary'>(viewMode);
  const [processStatus, setProcessStatus] = useState<string>('DRAFT');
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);

  const [complianceCriteria, setComplianceCriteria] = useState<ComplianceCriterion[]>([]);
  const [mandatoryRequirements, setMandatoryRequirements] = useState<MandatoryRequirement[]>([]);
  const [mainCriteria, setMainCriteria] = useState<MainCriterion[]>([]);
  const [scoringScale, setScoringScale] = useState<string>('0-5');
  const [evaluationMethodology, setEvaluationMethodology] = useState<string>('vfm');

  // Methodology options based on roadmap specifications
  // Section 1: Evaluation Criteria Scoring Scale
  const scoringScaleOptions = [
    {
      id: '0-5',
      name: '0-5 Point Scale',
      description: 'Traditional 5-point scoring scale',
      details: '0=Non-compliant, 1=Poor, 2=Below Standard, 3=Meets Requirements, 4=Exceeds, 5=Excellent',
      icon: '🔢'
    },
    {
      id: '0-10',
      name: '0-10 Point Scale', 
      description: 'Extended 10-point scoring for more granular evaluation',
      details: '0=Non-compliant, 5=Meets Requirements, 10=Excellent',
      icon: '🎯'
    },
    {
      id: 'requirements_based',
      name: 'Requirements-Based Normalisation',
      description: 'Converts 0-5 scores to percentages where "meets requirements" = 100%',
      details: 'Formula: (Raw Score ÷ 3) × 100. Score 3 = 100%, Score 5 = 166.67%',
      icon: '📋'
    }
  ];

  // Section 2: Supplier Ranking Methodology  
  const rankingMethodologyOptions = [
    {
      id: 'vfm',
      name: 'Value for Money (VFM)',
      description: 'Standard evaluation method focusing on quality score relative to cost',
      formula: 'VFM Ratio = Total Quality Score ÷ (Total Cost ÷ 1,000)',
      bestFor: 'Most procurement evaluations, straightforward value comparison',
      icon: '📊'
    },
    {
      id: 'price_quality',
      name: 'Price/Quality Strategic Analysis',
      description: 'Advanced method with baseline detection and quality premium analysis',
      formula: 'Quality Premium = (Cost Difference) ÷ (Quality Difference)',
      bestFor: 'Complex procurements, strategic decision-making, high-value contracts',
      icon: '💰'
    }
  ];

  const showNotification = useCallback((type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  }, []);

  useEffect(() => {
    const loadProcessData = async () => {
      setIsLoadingData(true);
      try {
        const token = Cookies.get('access_token');
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

        // Get process status and counts first
        const statusResponse = await axios.get(`${API_URL}/api/processes/${processId}/complete-data`, authHeaders);
        const statusData = statusResponse.data;
        setProcessStatus(statusData.status || 'DRAFT');

        // Load data directly from complete-data response
        
        // Load compliance criteria from complete-data response
        if (statusData.compliance_criteria && statusData.compliance_criteria.length > 0) {
          const loadedCompliance = statusData.compliance_criteria.map((crit: any) => ({
            description: crit.description,
            order_index: crit.order_index
          }));
          setComplianceCriteria(loadedCompliance);
        } else {
          setComplianceCriteria([]);
        }

        // Load mandatory requirements from complete-data response
        if (statusData.mandatory_requirements && statusData.mandatory_requirements.length > 0) {
          const loadedMandatory = statusData.mandatory_requirements.map((req: any) => ({
            requirement_text: req.description,
            order_index: req.order_index
          }));
          setMandatoryRequirements(loadedMandatory);
        } else {
          setMandatoryRequirements([]);
        }

        // Load main criteria from complete-data response
        if (statusData.main_criteria && statusData.main_criteria.length > 0) {
          const loadedCriteria = statusData.main_criteria.map((crit: any) => ({
            name: crit.name,
            description: crit.description || '',
            weight_percentage: crit.weight_percentage,
            order_index: crit.order_index,
            sub_criteria: crit.sub_criteria ? crit.sub_criteria.map((sub: any) => ({
              name: sub.name,
              description: sub.description || '',
              weight_percentage: sub.weight_percentage,
              order_index: sub.order_index
            })) : []
          }));
          setMainCriteria(loadedCriteria);
        } else {
          setMainCriteria([]);
        }

        // Suppliers are now managed separately via SupplierManagement component

        // Determine view mode based on criteria completion using count data
        const hasCompliance = statusData.compliance_criteria_count > 0;
        const hasMandatory = statusData.mandatory_requirements_count > 0;
        const hasEvaluation = statusData.main_criteria_count > 0;
        const allCriteriaComplete = hasCompliance && hasMandatory && hasEvaluation;


        // Set current step for setup view based on what exists
        if (hasEvaluation) {
            if (statusData.supplier_count > 0) setCurrentStep(5);
            else setCurrentStep(4);
        } else if (hasMandatory) {
            setCurrentStep(3);
        } else if (hasCompliance) {
            setCurrentStep(2);
        }

        // THEN determine view mode based on criteria completion
        if (allCriteriaComplete && viewMode === 'setup') {
          setCurrentViewMode('summary');
        }

        showNotification('success', 'Loaded existing process configuration');

      } catch (error: any) {
        console.error('Error loading process data:', error);
        showNotification('error', 'Failed to load existing process data. Starting with defaults.');
        setComplianceCriteria([]);
        setMandatoryRequirements([]);
        setMainCriteria([]);
      } finally {
        setIsLoadingData(false);
      }
    };

    loadProcessData();
  }, [processId, showNotification]);

  // Step 1: Compliance Criteria
  const addComplianceCriterion = () => setComplianceCriteria([...complianceCriteria, { description: "", order_index: complianceCriteria.length + 1 }]);
  const updateComplianceCriterion = (index: number, description: string) => {
    const updated = [...complianceCriteria];
    updated[index].description = description;
    setComplianceCriteria(updated);
  };
  const removeComplianceCriterion = (index: number) => {
    const updated = complianceCriteria.filter((_, i) => i !== index).map((crit, i) => ({ ...crit, order_index: i + 1 }));
    setComplianceCriteria(updated);
  };
  const saveComplianceCriteria = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const validCriteria = complianceCriteria.filter(crit => crit.description.trim() !== '');
      const apiPayload = { criteria: validCriteria.map(crit => ({ text: crit.description, order_index: crit.order_index })) };
      await axios.post(`${API_URL}/api/processes/${processId}/compliance-criteria`, apiPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', `Saved ${validCriteria.length} compliance criteria`);
      setCurrentStep(3);
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to save compliance criteria.');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Mandatory Requirements
  const addMandatoryRequirement = () => setMandatoryRequirements([...mandatoryRequirements, { requirement_text: "", order_index: mandatoryRequirements.length + 1 }]);
  const updateMandatoryRequirement = (index: number, requirement_text: string) => {
    const updated = [...mandatoryRequirements];
    updated[index].requirement_text = requirement_text;
    setMandatoryRequirements(updated);
  };
  const removeMandatoryRequirement = (index: number) => {
    const updated = mandatoryRequirements.filter((_, i) => i !== index).map((req, i) => ({ ...req, order_index: i + 1 }));
    setMandatoryRequirements(updated);
  };
  const saveMandatoryRequirements = async () => {
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const validRequirements = mandatoryRequirements.filter(req => req.requirement_text.trim() !== '');
      const apiPayload = { criteria: validRequirements.map(req => ({ text: req.requirement_text, order_index: req.order_index })) };
      await axios.post(`${API_URL}/api/processes/${processId}/mandatory-criteria`, apiPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', `Saved ${validRequirements.length} mandatory requirements`);
      setCurrentStep(4);
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to save mandatory requirements');
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Evaluation Criteria
  const addMainCriterion = () => setMainCriteria([...mainCriteria, { name: "", description: "", weight_percentage: 0, order_index: mainCriteria.length + 1, sub_criteria: [] }]);
  const updateMainCriterion = (index: number, field: keyof MainCriterion, value: any) => {
    const updated = [...mainCriteria];
    updated[index] = { ...updated[index], [field]: value };
    setMainCriteria(updated);
  };
  const removeMainCriterion = (index: number) => {
    const updated = mainCriteria.filter((_, i) => i !== index).map((crit, i) => ({ ...crit, order_index: i + 1 }));
    setMainCriteria(updated);
  };
  const addSubCriterion = (mainIndex: number) => {
    const updated = [...mainCriteria];
    updated[mainIndex].sub_criteria.push({ name: "", description: "", weight_percentage: 0, order_index: updated[mainIndex].sub_criteria.length + 1 });
    setMainCriteria(updated);
  };
  const updateSubCriterion = (mainIndex: number, subIndex: number, field: keyof SubCriterion, value: any) => {
    const updated = [...mainCriteria];
    updated[mainIndex].sub_criteria[subIndex] = { ...updated[mainIndex].sub_criteria[subIndex], [field]: value };
    setMainCriteria(updated);
  };
  const removeSubCriterion = (mainIndex: number, subIndex: number) => {
    const updated = [...mainCriteria];
    updated[mainIndex].sub_criteria = updated[mainIndex].sub_criteria.filter((_, i) => i !== subIndex).map((sub, i) => ({ ...sub, order_index: i + 1 }));
    setMainCriteria(updated);
  };

  const calculateTotalPercentages = useMemo(() => {
    const mainTotal = mainCriteria.reduce((sum, crit) => sum + (Number(crit.weight_percentage) || 0), 0);
    const subTotals = mainCriteria.map(criterion => ({
      name: criterion.name,
      total: criterion.sub_criteria.reduce((sum, sub) => sum + (Number(sub.weight_percentage) || 0), 0)
    }));
    return { mainTotal, subTotals };
  }, [mainCriteria]);

  const getValidationStatus = useCallback(() => {
    const totalMainWeight = mainCriteria.reduce((sum, crit) => sum + Number(crit.weight_percentage), 0);
    if (mainCriteria.length > 0 && Math.abs(totalMainWeight - 100) > 0.01) {
      return { isValid: false, message: `Main criteria weights must total 100%. Current total: ${totalMainWeight}%` };
    }
    for (const criterion of mainCriteria) {
      if (criterion.sub_criteria.length > 0) {
        const subTotal = criterion.sub_criteria.reduce((sum, sub) => sum + Number(sub.weight_percentage), 0);
        if (Math.abs(subTotal - 100) > 0.01) {
          return { isValid: false, message: `Sub-criteria weights for "${criterion.name}" must total 100%. Current total: ${subTotal}%` };
        }
      }
    }
    return { isValid: true, message: 'Weights are valid' };
  }, [mainCriteria]);

  const isWeightsValid = useMemo(() => getValidationStatus().isValid, [getValidationStatus]);

  const saveMainCriteria = async (moveToStep: 4 | 5) => {
    const validation = getValidationStatus();
    if (!validation.isValid) {
      showNotification('error', validation.message);
      return;
    }
    setIsLoading(true);
    try {
      const token = Cookies.get('access_token');
      const validCriteria = mainCriteria.filter(crit => crit.name.trim() !== '');
      if (validCriteria.length === 0) {
        showNotification('error', 'Please add at least one main criterion');
        setIsLoading(false);
        return;
      }
      const apiPayload = { 
        criteria: validCriteria.map(crit => ({ 
          name: crit.name,
          description: crit.description,
          weight_percentage: crit.weight_percentage,
          order_index: crit.order_index,
          sub_criteria: crit.sub_criteria.map(sub => ({
            name: sub.name,
            description: sub.description,
            weight_percentage: sub.weight_percentage,
            order_index: sub.order_index
          }))
        })) 
      };
      await axios.post(`${API_URL}/api/processes/${processId}/evaluation-criteria`, apiPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showNotification('success', `Saved ${validCriteria.length} main criteria`);
      setCurrentStep(moveToStep);
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'Failed to save main criteria');
    } finally {
      setIsLoading(false);
    }
  };


  // Step 5: Completion
  const completeOrSaveSetup = async () => {
    setIsLoading(true);
    try {
      showNotification('success', 'Process setup completed successfully! Add suppliers from the dashboard to activate the process.');
      setTimeout(() => onComplete(), 2000);
    } catch (error: any) {
      showNotification('error', error.response?.data?.detail || 'An error occurred while completing setup.');
    } finally {
      setIsLoading(false);
    }
  };

  // Summary View Component
  const renderSummaryView = () => {
    const isReadOnly = processStatus !== 'DRAFT';
    
    return (
      <div style={styles.contentWrapper}>
        <h1 style={styles.pageTitle}>Process Overview: {processName}</h1>
        <p style={{ textAlign: 'center', color: 'var(--color-text-on-gradient-secondary)', marginBottom: 'var(--spacing-xl)' }}>
          Status: <strong>{processStatus}</strong> • Review and edit process configuration
        </p>

        <div style={{ display: 'grid', gap: 'var(--spacing-lg)', marginBottom: 'var(--spacing-xl)' }}>
          
          {/* Compliance Criteria Section */}
          <div style={{...styles.card, opacity: isReadOnly ? 0.7 : 1}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
              <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Compliance Criteria</h3>
              {!isReadOnly && (
                <button 
                  onClick={() => { setCurrentViewMode('setup'); setCurrentStep(1); }}
                  style={{...styles.button, ...styles.buttonSecondary, padding: '0.5rem 1rem'}}
                >
                  ✏️ Edit
                </button>
              )}
            </div>
            <div style={{ color: 'var(--color-text-secondary)' }}>
              {complianceCriteria.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {complianceCriteria.map((criterion, index) => (
                    <li key={index} style={{ marginBottom: '0.5rem' }}>{criterion.description}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0, fontStyle: 'italic' }}>No compliance criteria defined</p>
              )}
            </div>
          </div>

          {/* Mandatory Requirements Section */}
          <div style={{...styles.card, opacity: isReadOnly ? 0.7 : 1}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
              <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Mandatory Requirements</h3>
              {!isReadOnly && (
                <button 
                  onClick={() => { setCurrentViewMode('setup'); setCurrentStep(2); }}
                  style={{...styles.button, ...styles.buttonSecondary, padding: '0.5rem 1rem'}}
                >
                  ✏️ Edit
                </button>
              )}
            </div>
            <div style={{ color: 'var(--color-text-secondary)' }}>
              {mandatoryRequirements.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: '1.5rem' }}>
                  {mandatoryRequirements.map((req, index) => (
                    <li key={index} style={{ marginBottom: '0.5rem' }}>{req.requirement_text}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0, fontStyle: 'italic' }}>No mandatory requirements defined</p>
              )}
            </div>
          </div>

          {/* Evaluation Criteria Section */}
          <div style={{...styles.card, opacity: isReadOnly ? 0.7 : 1}}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)' }}>
              <h3 style={{ margin: 0, color: 'var(--color-primary)' }}>Evaluation Criteria</h3>
              {!isReadOnly && (
                <button 
                  onClick={() => { setCurrentViewMode('setup'); setCurrentStep(3); }}
                  style={{...styles.button, ...styles.buttonSecondary, padding: '0.5rem 1rem'}}
                >
                  ✏️ Edit
                </button>
              )}
            </div>
            <div style={{ color: 'var(--color-text-secondary)' }}>
              {mainCriteria.length > 0 ? (
                <div>
                  {mainCriteria.map((criterion, index) => (
                    <div key={index} style={{ marginBottom: '1rem', padding: '0.5rem', background: '#f8f9fa', borderRadius: '6px' }}>
                      <strong>{criterion.name}</strong> ({criterion.weight_percentage}%)
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.9rem' }}>{criterion.description}</p>
                      {criterion.sub_criteria.length > 0 && (
                        <ul style={{ margin: '0.5rem 0 0 0', paddingLeft: '1.5rem', fontSize: '0.85rem' }}>
                          {criterion.sub_criteria.map((sub, subIndex) => (
                            <li key={subIndex}>{sub.name} ({sub.weight_percentage}%)</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontStyle: 'italic' }}>No evaluation criteria defined</p>
              )}
            </div>
          </div>

        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onBack} style={{...styles.button, ...styles.buttonSecondary}}>
            ← Back to Dashboard
          </button>
          <button onClick={onComplete} style={{...styles.button, background: 'var(--color-success)'}}>
            Complete Setup ✅
          </button>
        </div>
      </div>
    );
  };
  
  // Show summary view if currentViewMode is 'summary'
  if (currentViewMode === 'summary') {
    return (
      <div style={styles.mainContainer} className="density-compact">
        {notification && (
          <div style={{ 
            position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000, 
            background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', 
            color: 'white', padding: '1rem 1.5rem', borderRadius: '12px', 
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)' 
          }}>
            {notification.message}
          </div>
        )}
        {renderSummaryView()}
      </div>
    );
  }
  
  return (
    <div style={styles.mainContainer} className="density-compact">
      {notification && (
        <div style={{ 
          position: 'fixed', top: '1rem', right: '1rem', zIndex: 1000, 
          background: notification.type === 'success' ? 'var(--color-success)' : 'var(--color-error)', 
          color: 'white', padding: '1rem 1.5rem', borderRadius: 'var(--radius-lg)', 
          boxShadow: 'var(--shadow-lg)' 
        }}>
          {notification.message}
        </div>
      )}

      {isLoadingData ? (
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <div style={{...styles.card, maxWidth: '400px', margin: 'auto', padding: '3rem'}}>
                <div className="loading-spinner" style={{ margin: '0 auto 1rem auto' }}></div>
                <div style={{ fontSize: '1.2rem', color: 'var(--color-text-primary)' }}>Loading Process Data...</div>
            </div>
          </div>
      ) : (
        <>
          <div style={{ 
            background: 'rgba(255,255,255,0.6)', 
            padding: '1.5rem 2rem', 
            borderBottom: '1px solid var(--color-surface-variant)' 
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', maxWidth: '1000px', margin: '0 auto' }}>
              {[{ step: 1, title: 'Methodology' }, { step: 2, title: 'Compliance' }, { step: 3, title: 'Mandatory' }, { step: 4, title: 'Criteria' }, { step: 5, title: 'Complete' }].map(({ step, title }, index, arr) => {
                // Determine if step is accessible (completed or current)
                const isAccessible = step <= Math.max(currentStep, 1); // Always allow step 1
                const isClickable = isAccessible && step !== currentStep;
                
                return (
                  <React.Fragment key={step}>
                      <div 
                        style={{ 
                          textAlign: 'center', 
                          color: currentStep >= step ? 'var(--color-primary)' : 'var(--color-text-disabled)',
                          cursor: isClickable ? 'pointer' : 'default',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => {
                          if (isClickable) {
                            setCurrentStep(step);
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (isClickable) {
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (isClickable) {
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                      >
                          <div style={{ 
                            width: '2.5rem', height: '2.5rem', borderRadius: '50%', 
                            background: currentStep >= step ? 'var(--color-primary)' : 'var(--color-surface-variant)', 
                            color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', 
                            margin: '0 auto 0.5rem auto', 
                            border: currentStep === step ? '4px solid var(--color-primary-light)' : '4px solid transparent', 
                            fontWeight: 'bold', fontSize: '1.2rem',
                            boxShadow: isClickable ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s ease'
                          }}>
                            {step}
                          </div>
                          <p style={{ 
                            margin: 0, 
                            fontSize: '0.9rem', 
                            fontWeight: currentStep === step ? 'bold' : 'normal',
                            opacity: isAccessible ? 1 : 0.6
                          }}>
                            {title}
                          </p>
                      </div>
                    {index < arr.length - 1 && (
                      <div style={{ 
                        flex: 1, height: '4px', 
                        background: currentStep > step ? 'var(--color-primary)' : 'var(--color-surface-variant)', 
                        borderRadius: '2px', transform: 'translateY(-1rem)' 
                      }}></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <main style={{ padding: '2rem' }}>
            <div style={{ maxWidth: '900px', margin: '0 auto' }}>
              <div style={styles.card}>
                <h2 className="page-title">Process Setup: {processName}</h2>
                
                  {currentStep === 1 && (
                    <div>
                      <h3 style={{ color: 'var(--color-text-primary)', fontSize: '1.5rem', fontWeight: 'bold' }}>Step 1: Evaluation Configuration</h3>
                      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem' }}>Configure how evaluation criteria will be scored and how suppliers will be ranked.</p>
                      
                      {/* Section 1: Scoring Scale Selection */}
                      <div style={{ marginBottom: '3rem' }}>
                        <h4 style={{ color: '#1f2937', fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                          🔢 Section 1: Evaluation Criteria Scoring Scale
                        </h4>
                        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
                          Choose the point scale evaluators will use to score each criterion.
                        </p>
                        
                        <div style={{ display: 'grid', gap: '1rem', marginBottom: '1rem' }}>
                          {scoringScaleOptions.map((scale) => (
                            <div
                              key={scale.id}
                              onClick={() => setScoringScale(scale.id)}
                              style={{
                                padding: '1rem',
                                border: `2px solid ${scoringScale === scale.id ? '#2563eb' : '#e5e7eb'}`,
                                borderRadius: '8px',
                                background: scoringScale === scale.id ? '#eff6ff' : 'white',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ fontSize: '1.5rem' }}>{scale.icon}</div>
                                <div>
                                  <h5 style={{ 
                                    margin: '0 0 0.25rem 0', 
                                    color: scoringScale === scale.id ? '#2563eb' : '#374151',
                                    fontWeight: 'bold'
                                  }}>
                                    {scale.name}
                                    {scoringScale === scale.id && (
                                      <span style={{ 
                                        background: '#2563eb', 
                                        color: 'white', 
                                        padding: '0.25rem 0.5rem', 
                                        borderRadius: '9999px', 
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        marginLeft: '0.5rem'
                                      }}>
                                        SELECTED
                                      </span>
                                    )}
                                  </h5>
                                  <p style={{ margin: '0 0 0.5rem 0', color: '#6b7280', fontSize: '0.9rem' }}>
                                    {scale.description}
                                  </p>
                                  <div style={{ 
                                    fontSize: '0.8rem', 
                                    color: '#374151',
                                    background: scoringScale === scale.id ? '#dbeafe' : '#f9fafb',
                                    padding: '0.5rem',
                                    borderRadius: '4px'
                                  }}>
                                    {scale.details}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Section 2: Ranking Methodology Selection */}
                      <div>
                        <h4 style={{ color: '#1f2937', fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
                          📊 Section 2: Supplier Ranking Methodology
                        </h4>
                        <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem' }}>
                          Choose how evaluation scores and pricing will be combined to rank suppliers.
                        </p>
                        
                        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
                          {rankingMethodologyOptions.map((methodology) => (
                          <div
                            key={methodology.id}
                            onClick={() => setEvaluationMethodology(methodology.id)}
                            style={{
                              padding: '1.5rem',
                              border: `2px solid ${evaluationMethodology === methodology.id ? '#2563eb' : '#e5e7eb'}`,
                              borderRadius: '12px',
                              background: evaluationMethodology === methodology.id ? '#eff6ff' : 'white',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                              <div style={{ fontSize: '2rem' }}>{methodology.icon}</div>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                  <h4 style={{ 
                                    margin: 0, 
                                    color: evaluationMethodology === methodology.id ? '#2563eb' : '#374151',
                                    fontWeight: 'bold'
                                  }}>
                                    {methodology.name}
                                  </h4>
                                  {evaluationMethodology === methodology.id && (
                                    <span style={{ 
                                      background: '#2563eb', 
                                      color: 'white', 
                                      padding: '0.25rem 0.5rem', 
                                      borderRadius: '9999px', 
                                      fontSize: '0.8rem',
                                      fontWeight: 'bold'
                                    }}>
                                      SELECTED
                                    </span>
                                  )}
                                </div>
                                <p style={{ 
                                  margin: '0 0 0.75rem 0', 
                                  color: '#6b7280', 
                                  lineHeight: '1.5' 
                                }}>
                                  {methodology.description}
                                </p>
                                <div style={{ 
                                  background: evaluationMethodology === methodology.id ? '#dbeafe' : '#f9fafb',
                                  padding: '0.75rem',
                                  borderRadius: '6px',
                                  marginBottom: '0.75rem'
                                }}>
                                  <strong style={{ color: '#374151', fontSize: '0.9rem' }}>Formula:</strong>
                                  <div style={{ 
                                    fontFamily: 'monospace', 
                                    fontSize: '0.85rem', 
                                    color: '#1f2937',
                                    marginTop: '0.25rem'
                                  }}>
                                    {methodology.formula}
                                  </div>
                                </div>
                                <div style={{ fontSize: '0.9rem', color: '#059669' }}>
                                  <strong>Best For:</strong> {methodology.bestFor}
                                </div>
                              </div>
                            </div>
                          </div>
                          ))}
                        </div>
                      </div>
                      
                      <div style={{
                        background: '#f0f9ff',
                        border: '1px solid #bfdbfe',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem'
                      }}>
                        <h5 style={{ margin: '0 0 0.5rem 0', color: '#1e40af' }}>💡 Need Help Choosing?</h5>
                        <ul style={{ margin: 0, paddingLeft: '1.25rem', color: '#475569', fontSize: '0.9rem' }}>
                          <li><strong>Standard Procurement:</strong> Use VFM for straightforward value comparison</li>
                          <li><strong>Strategic/Complex:</strong> Use Price/Quality for advanced premium analysis</li>
                          <li><strong>Government/Compliance:</strong> Use Requirements-Based for regulatory clarity</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div>
                      <h3 style={{ color: 'var(--color-text-primary)', fontSize: '1.5rem', fontWeight: 'bold' }}>Step 2: Compliance Criteria</h3>
                      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem' }}>Define the pass/fail compliance criteria checked by the Procurement Specialist.</p>
                      {complianceCriteria.map((crit, index) => (
                        <div key={index} style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input 
                            type="text" 
                            value={crit.description} 
                            onChange={(e) => updateComplianceCriterion(index, e.target.value)} 
                            placeholder="Enter compliance criterion..." 
                            className="input"
                          />
                          <button 
                            onClick={() => removeComplianceCriterion(index)} 
                            className="btn btn-destructive"
                            style={{ padding: '16px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button 
                        onClick={addComplianceCriterion} 
                        className="btn"
                        style={{ background: 'linear-gradient(45deg, #10b981, #059669)', marginTop: '1rem' }}
                      >
                        + Add Criterion
                      </button>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div>
                      <h3 style={{ color: 'var(--color-text-primary)', fontSize: '1.5rem', fontWeight: 'bold' }}>Step 3: Mandatory Requirements</h3>
                      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem' }}>Define requirements evaluators will check. If none are added, this step is skipped.</p>
                      {mandatoryRequirements.map((req, index) => (
                        <div key={index} style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <input 
                            type="text" 
                            value={req.requirement_text} 
                            onChange={(e) => updateMandatoryRequirement(index, e.target.value)} 
                            placeholder="Enter mandatory requirement..." 
                            className="input"
                          />
                          <button 
                            onClick={() => removeMandatoryRequirement(index)} 
                            className="btn btn-destructive"
                            style={{ padding: '16px' }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button 
                        onClick={addMandatoryRequirement} 
                        className="btn"
                        style={{ background: 'linear-gradient(45deg, #10b981, #059669)', marginTop: '1rem' }}
                      >
                        + Add Requirement
                      </button>
                    </div>
                  )}

                  {currentStep === 4 && (
                    <div>
                      <h3 style={{ color: 'var(--color-text-primary)', fontSize: '1.5rem', fontWeight: 'bold' }}>Step 4: Evaluation Criteria</h3>
                      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem' }}>Define weighted criteria. Weights must total 100%.</p>
                      
                      {/* Floating weight totals */}
                      <div 
                        style={{
                          position: 'fixed',
                          top: '50%',
                          right: '2rem',
                          transform: 'translateY(-50%)',
                          zIndex: 1000,
                          background: 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(10px)',
                          border: '2px solid',
                          borderColor: Math.abs(calculateTotalPercentages.mainTotal - 100) < 0.01 ? 'var(--color-success)' : 'var(--color-error)',
                          borderRadius: '12px',
                          padding: '1rem',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                          minWidth: '200px',
                          textAlign: 'center',
                          fontSize: '1rem',
                          fontWeight: 'bold',
                          color: Math.abs(calculateTotalPercentages.mainTotal - 100) < 0.01 ? 'var(--color-success)' : 'var(--color-error)'
                        }}
                      >
                        <div style={{ marginBottom: '0.5rem' }}>
                          📊 Weight Total
                        </div>
                        <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
                          {calculateTotalPercentages.mainTotal.toFixed(1)}% / 100%
                        </div>
                        {Math.abs(calculateTotalPercentages.mainTotal - 100) >= 0.01 && (
                          <div style={{ 
                            fontSize: '0.85rem', 
                            fontWeight: 'normal',
                            opacity: 0.8 
                          }}>
                            {calculateTotalPercentages.mainTotal > 100 ? 
                              `Over by ${(calculateTotalPercentages.mainTotal - 100).toFixed(1)}%` : 
                              `Need ${(100 - calculateTotalPercentages.mainTotal).toFixed(1)}% more`
                            }
                          </div>
                        )}
                        {Math.abs(calculateTotalPercentages.mainTotal - 100) < 0.01 && (
                          <div style={{ 
                            fontSize: '0.85rem', 
                            fontWeight: 'normal',
                            opacity: 0.8 
                          }}>
                            ✅ Perfect!
                          </div>
                        )}
                      </div>

                      {mainCriteria.map((criterion, mainIndex) => (
                        <div key={mainIndex} style={{ 
                          border: '1px solid var(--color-surface-variant)', 
                          borderRadius: 'var(--radius-lg)', 
                          padding: 'var(--spacing-lg)', 
                          marginBottom: '1rem', 
                          background: 'var(--color-surface-variant)' 
                        }}>
                          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center' }}>
                            <input 
                              type="text" 
                              value={criterion.name} 
                              onChange={(e) => updateMainCriterion(mainIndex, 'name', e.target.value)} 
                              placeholder="Criterion name" 
                              className="input"
                              style={{ fontWeight: 'bold' }}
                            />
                            <input 
                              type="number" 
                              min="0"
                              max="100"
                              step="0.1"
                              value={criterion.weight_percentage} 
                              onChange={(e) => updateMainCriterion(mainIndex, 'weight_percentage', parseFloat(e.target.value) || 0)} 
                              placeholder="Weight %" 
                              className="input"
                              style={{ width: '120px' }}
                            />
                            <button 
                              onClick={() => removeMainCriterion(mainIndex)} 
                              className="btn btn-destructive"
                              style={{ padding: '16px' }}
                            >
                              ×
                            </button>
                          </div>
                          <textarea 
                            value={criterion.description} 
                            onChange={(e) => updateMainCriterion(mainIndex, 'description', e.target.value)} 
                            placeholder="Criterion description..." 
                            rows={2} 
                            className="textarea"
                            style={{ marginBottom: '1rem' }}
                          />
                          
                          {criterion.sub_criteria.length > 0 && (
                            <div style={{
                              ...styles.percentageDisplay,
                              fontSize: '0.9rem',
                              margin: '0.5rem 0 1rem 2rem',
                              ...(Math.abs(calculateTotalPercentages.subTotals[mainIndex]?.total - 100) < 0.01 ? styles.percentageValid : styles.percentageInvalid)
                            }}>
                              Sub-criteria Total: {calculateTotalPercentages.subTotals[mainIndex]?.total.toFixed(1) || 0}% / 100%
                              {Math.abs((calculateTotalPercentages.subTotals[mainIndex]?.total || 0) - 100) >= 0.01 && (
                                <div style={{ fontSize: '0.8rem', marginTop: '0.3rem' }}>
                                  {(calculateTotalPercentages.subTotals[mainIndex]?.total || 0) > 100 ? 
                                    `Over by ${((calculateTotalPercentages.subTotals[mainIndex]?.total || 0) - 100).toFixed(1)}%` : 
                                    `Need ${(100 - (calculateTotalPercentages.subTotals[mainIndex]?.total || 0)).toFixed(1)}% more`
                                  }
                                </div>
                              )}
                            </div>
                          )}
                          
                          {criterion.sub_criteria.map((sub, subIndex) => (
                            <div key={subIndex} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', marginLeft: '2rem', alignItems: 'center' }}>
                              <input 
                                type="text" 
                                value={sub.name} 
                                onChange={(e) => updateSubCriterion(mainIndex, subIndex, 'name', e.target.value)} 
                                placeholder="Sub-criterion name" 
                                className="input"
                                style={{ fontSize: '0.9rem' }}
                              />
                              <input 
                                type="number" 
                                min="0"
                                max="100"
                                step="0.1"
                                value={sub.weight_percentage} 
                                onChange={(e) => updateSubCriterion(mainIndex, subIndex, 'weight_percentage', parseFloat(e.target.value) || 0)} 
                                placeholder="Weight %" 
                                className="input"
                                style={{ width: '100px', fontSize: '0.9rem' }}
                              />
                              <button 
                                onClick={() => removeSubCriterion(mainIndex, subIndex)} 
                                className="btn btn-destructive btn-sm"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                          <button 
                            onClick={() => addSubCriterion(mainIndex)} 
                            className="btn btn-sm"
                            style={{ 
                              background: 'linear-gradient(45deg, #8b5cf6, #7c3aed)', 
                              marginLeft: '2rem', 
                              marginTop: '0.5rem' 
                            }}
                          >
                            + Add Sub-criterion
                          </button>
                        </div>
                      ))}
                      <button 
                        onClick={addMainCriterion} 
                        className="btn"
                        style={{ background: 'linear-gradient(45deg, #10b981, #059669)', marginTop: '1rem' }}
                      >
                        + Add Main Criterion
                      </button>
                    </div>
                  )}

                  {currentStep === 5 && (
                    <div>
                      <h3 style={{ color: 'var(--color-text-primary)', fontSize: '1.5rem', fontWeight: 'bold' }}>Step 5: Complete Process Setup</h3>
                      <p style={{ color: 'var(--color-text-secondary)', marginBottom: '2rem' }}>Review your configuration and save or activate the process.</p>
                      <div style={{ 
                        background: 'var(--color-surface-variant)', 
                        padding: 'var(--spacing-lg)', 
                        borderRadius: 'var(--radius-lg)', 
                        border: '1px solid var(--color-surface-variant)', 
                        marginBottom: '2rem' 
                      }}>
                        <h4 style={{ margin: '0 0 1rem 0', color: 'var(--color-text-primary)' }}>Process Summary</h4>
                        <p><strong>Compliance Criteria:</strong> {complianceCriteria.filter(c => c.description.trim() !== '').length}</p>
                        <p><strong>Mandatory Requirements:</strong> {mandatoryRequirements.filter(r => r.requirement_text.trim() !== '').length}</p>
                        <p><strong>Main Criteria:</strong> {mainCriteria.filter(c => c.name.trim() !== '').length}</p>
                      </div>
                    </div>
                  )}

                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginTop: '2rem', 
                  paddingTop: '2rem', 
                  borderTop: '1px solid var(--color-surface-variant)' 
                }}>
                    <div>
                        {currentStep > 1 && (
                          <button 
                            onClick={() => setCurrentStep(currentStep - 1)} 
                            className="btn btn-secondary"
                          >
                            ← Previous
                          </button>
                        )}
                    </div>
                    
                    <div style={{display: 'flex', gap: '1rem'}}>
                        {currentStep === 1 && (
                          <button 
                            onClick={async () => {
                              setIsLoading(true);
                              try {
                                const token = Cookies.get('access_token');
                                await axios.patch(`${API_URL}/api/processes/${processId}`, 
                                  { 
                                    evaluation_methodology: evaluationMethodology,
                                    score_scale: scoringScale 
                                  },
                                  { headers: { Authorization: `Bearer ${token}` } }
                                );
                                showNotification('success', `Selected ${scoringScale} scale and ${rankingMethodologyOptions.find(m => m.id === evaluationMethodology)?.name} methodology`);
                                setCurrentStep(2);
                              } catch (error) {
                                showNotification('error', 'Failed to save evaluation configuration');
                              } finally {
                                setIsLoading(false);
                              }
                            }}
                            disabled={isLoading} 
                            className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'}`}
                          >
                            {isLoading ? 'Saving...' : `Continue with Configuration →`}
                          </button>
                        )}
                        {currentStep === 2 && (
                          <button 
                            onClick={saveComplianceCriteria} 
                            disabled={isLoading} 
                            className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'}`}
                          >
                            {isLoading ? 'Saving...' : 'Save & Continue →'}
                          </button>
                        )}
                        {currentStep === 3 && (
                          <button 
                            onClick={saveMandatoryRequirements} 
                            disabled={isLoading} 
                            className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'}`}
                          >
                            {isLoading ? 'Saving...' : 'Save & Continue →'}
                          </button>
                        )}
                        {currentStep === 4 && (
                          <button 
                            onClick={() => saveMainCriteria(5)} 
                            disabled={isLoading || !isWeightsValid} 
                            className={`btn ${(isLoading || !isWeightsValid) ? 'btn-disabled' : 'btn-primary'}`}
                          >
                            {isLoading ? 'Saving...' : 'Complete Setup →'}
                          </button>
                        )}
                        {currentStep === 5 && (
                            <button 
                              onClick={completeOrSaveSetup} 
                              disabled={isLoading} 
                              className={`btn ${isLoading ? 'btn-disabled' : 'btn-primary'}`}
                              style={{
                                padding: '1rem 2rem', 
                                fontSize: '1.2rem', 
                                background: isLoading ? 'var(--color-text-disabled)' : 'linear-gradient(45deg, #2563eb, #059669)'
                              }}
                            >
                              {isLoading ? 'Saving...' : '✅ Complete Process Setup'}
                            </button>
                        )}
                    </div>
                </div>
              </div>
            </div>
          </main>
        </>
      )}
    </div>
  );
};

export default ProcessSetup;